<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    selectAll: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    options: {
        type: Array,
        default: () => []
    },
    visible: {
        type: Boolean,
        default: true
    },
    width: {
        type: String,
        default: '12.5rem'
    },
    placeholder: {
        type: String,
        default: ''
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    padding: {
        type: String,
        default: '0.7rem'
    }
});
const selectWidth = computed(() => props.width || '12.5rem');
const isReadonly = computed(() => props.readonly);
const emit = defineEmits(['update:modelValue', 'input', 'enter', 'change', 'update-options']);
const selectedValue = ref(props.modelValue);
const items = ref([]);
const errMessage = ref(props.errmsg);
const ValidationMsg = ref('');

const updateItems = (options) => {
    if (Array.isArray(options)) {
        items.value = options.map((option) => ({
            value: option.value.toString(),
            label: option.disptext && option.disptext !== '' ? option.disptext : option.text
        }));
    } else {
        items.value = [];
    }
};

watch(
    () => props.options,
    (newOptions) => {
        updateItems(newOptions || []);
        // options이 변경되었을때, modelValue가 options에 없는 값이면, modelValue를 초기화.
        if (props.modelValue && !items.value.some((item) => item.value === props.modelValue.toString())) {
            emit('update:modelValue', '');
        }
    },
    { immediate: true, deep: true }
);

watch(
    () => props.modelValue,
    (newValue) => {
        selectedValue.value = newValue === null || newValue === undefined ? '' : newValue.toString();
    },
    { deep: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onChange = (event) => {
    ValidationMsg.value = '';
    const value = event.value.toString();
    selectedValue.value = value;
    const selectedOption = items.value.find((item) => item.value === value.toString());

    emit('change', {
        id: props.inputId,
        value: value
        //label: selectedOption ? selectedOption.value : value
    });

    emit('update:modelValue', value);
};

onMounted(() => {
    // 초기값 설정
    // if (props.modelValue === '' && items.value.length > 0) {
    //     // selectAll 옵션이 있으면 그것을 선택, 없으면 첫 번째 옵션 선택
    //     selectedValue.value = ''; // props.selectAll ? '' : items.value[0].value;
    //     emit('update:modelValue', selectedValue.value);
    // }
});

const getValueForTitle = (value) => {
    const item = items.value.find((item) => item.value === value);
    return item ? item.value : '';
};

const clearInput = () => {
    selectedValue.value = '';
    emit('input', { id: props.inputId, value: '' });
    emit('update:modelValue', '');
};

const emptyMessage = computed(() => {
    return props.label ? `${props.label}에 등록된 항목이 없습니다.` : '등록된 항목이 없습니다.';
});

const handleDropdownShow = async () => {
    await nextTick();

    const filterInput = document.querySelector('.p-select-filter');

    if (filterInput) {
        filterInput.focus();
    }
};

const itemCount = computed(() => items.value.length);
</script>

<template>
    <div v-if="visible" class="flex flex-col" :style="{ marginTop, marginBottom, marginLeft, marginRight }">
        <label :class="['label mb-1 mx-1', required ? 'label-required' : 'text-gray-700 dark:text-gray-300']">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>
        <div class="relative w-full flex items-center h-12">
            <Select
                :id="inputId"
                v-model="selectedValue"
                :options="items"
                optionLabel="label"
                optionValue="value"
                :title="getValueForTitle(selectedValue)"
                :disabled="isReadonly"
                @change="onChange"
                class="p-select h-[2.5rem]"
                :class="['custom-select-input', disabled ? 'disabled' : '', { 'error-border': ValidationMsg }, { 'readonly-Bg': isReadonly }]"
                :placeholder="props.placeholder === '' ? '선택하세요.' : props.placeholder"
                filter
                @show="handleDropdownShow"
            >
                <template #empty>
                    {{ emptyMessage }}
                </template>
                <template #footer>
                    <div class="dropdown-footer">Total {{ items.length }}</div>
                </template>
            </Select>
            <button v-if="selectedValue && !isReadonly" @click="clearInput" class="clear-button">&times;</button>
        </div>
    </div>
</template>

<style scoped>
.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    color: #35424a;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    user-select: none;
}

.error-border {
    border-color: #f87171 !important; /* 빨간색 테두리 */
}
.readonly-Bg {
    background-color: whitesmoke !important;
}
.custom-select-input {
    width: 100%;
    font-size: 0.9rem;
    font-family: 'SCoreDream400';
    background-color: white;
    border-radius: 0.5rem;
    border: 1px solid #d1d5db;
    cursor: pointer;
}

.custom-select :deep(.p-select) {
    font-size: 0.9rem;
    font-family: 'SCoreDream400';
    background-color: white;
    border: 1px solid #d1d5db;
    margin: 0.25rem 0;
}

/* .custom-select :deep(.p-inputtext) {
    width: 100%;
    height: 3rem;
    padding: 0.75rem 2.5rem 0.75rem 1rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    transition:
        border-color 0.2s,
        box-shadow 0.2s;
} */
.custom-select :deep(.p-inputwrapper-focus) {
    border-color: #0038ea !important;
}

.custom-select :deep(.p-select-label) {
    font-family: 'SCoreDream400';
    display: flex;
    align-items: center; /* 상하 중앙정렬 */
    padding: 0 !important;
}

.custom-select :deep(.p-dropdown-item) {
    padding: 0.5rem;
    display: flex;
    align-items: center; /* 상하 중앙정렬 */
}

.validation-message {
    color: #f87171; /* red-500 */
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.dropdown-footer {
    font-size: 0.8rem;
    color: gray;
    padding: 0.2rem;
    text-align: right;
    /* border-top: 0.1rem solid #d1d5db; */
    margin-right: 0.5rem;
}
.clear-button {
    position: absolute;
    right: 2.5rem; /* AutoComplete의 드롭다운 아이콘을 고려한 위치 */
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    width: 1rem;
    height: 1rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    background: transparent;
    border: none;
    opacity: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1; /* AutoComplete 컴포넌트 위에 표시되도록 */
}

.clear-button:hover {
    font-family: 'SCoreDream500';
    opacity: 1;
}

.p-select.p-disabled .p-select-label.p-placeholder {
    color: red !important;
}
</style>
